import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Save, Plus } from 'lucide-react';
import Select from 'react-select';
import { INSTRUCTION_BUILDER_TABS } from '../../../helpers/constants';

const municipalitySelectStyles = {
  container: base => ({
    ...base,
    minWidth: '180px',
    flex: 1
  }),
  control: base => ({
    ...base,
    minHeight: '34px',
    height: '34px',
    fontSize: '13px',
    borderRadius: '10px',
    boxShadow: 'none'
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 8px'
  }),
  menu: base => ({
    ...base,
    zIndex: 1060
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1060
  })
};

const InstructionHeader = ({
  selectedCounty,
  selectedMunicipality,
  municipalityOptions,
  onMunicipalityChange,
  showHidden,
  onShowHiddenChange,
  onAddStep,
  onSave,
  saving,
  hasUnsavedChanges,
  activeTab,
  onTabChange,
  allSteps
}) => {
  const tabCounts = React.useMemo(() => {
    const counts = {
      All: allSteps.filter(s => !s.isHidden).length
    };

    INSTRUCTION_BUILDER_TABS.forEach(tab => {
      counts[tab] = allSteps.filter(s => s.tab === tab && !s.isHidden).length;
    });

    return counts;
  }, [allSteps]);

  return (
    <div>
      <div
        className="header-bar d-flex align-items-center justify-content-between flex-wrap bg-white border-bottom py-3"
        style={{ gap: '20px', borderRadius: '14px 14px 0 0' }}
      >
        <div className="d-flex align-items-center flex-fill" style={{ gap: '16px' }}>
          <div
            className="d-flex align-items-center px-2 py-1"
            style={{ background: 'var(--info-light)', border: '1px solid var(--info)', borderRadius: '10px' }}
          >
            <span className="font-weight-bold mr-2 midFont" style={{ color: '#1e40af' }}>
              County:
            </span>
            <span className="font-weight-bold midFont" style={{ color: '#1e3a8a' }}>
              {selectedCounty || 'Not Selected'}
            </span>
          </div>

          <div className="d-flex align-items-center" style={{ gap: '8px', minWidth: '300px' }}>
            <span className="text-muted font-weight-bold midFont text-nowrap">Municipality:</span>
            <Select
              classNamePrefix="select"
              className="basic-single select-organization flex-fill"
              placeholder="Select municipality"
              options={municipalityOptions}
              value={municipalityOptions?.find(option => option.value === selectedMunicipality) || null}
              onChange={option => onMunicipalityChange?.(option?.value || '')}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              isClearable
              styles={municipalitySelectStyles}
            />
          </div>
        </div>

        <div className="d-flex align-items-center ml-auto" style={{ gap: '10px' }}>
          <label
            className="toggle-wrap d-flex align-items-center mb-0"
            style={{ gap: '7px', cursor: 'pointer', userSelect: 'none' }}
          >
            <span className="text-muted font-weight-bold" style={{ fontSize: '12px' }}>
              Show Hidden
            </span>
            <div className="position-relative" style={{ width: '34px', height: '19px' }}>
              <input
                type="checkbox"
                checked={showHidden}
                onChange={e => onShowHiddenChange(e.target.checked)}
                className="position-absolute"
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                className="position-absolute rounded-pill"
                style={{
                  inset: 0,
                  background: showHidden ? '#2563eb' : '#d1d5db',
                  transition: 'background 0.18s'
                }}
              >
                <span
                  className="position-absolute rounded-circle bg-white shadow-sm"
                  style={{
                    width: '15px',
                    height: '15px',
                    left: '2px',
                    top: '2px',
                    transition: 'transform 0.18s',
                    transform: showHidden ? 'translateX(15px)' : 'translateX(0)'
                  }}
                />
              </span>
            </div>
          </label>

          <Button
            variant="success"
            onClick={onAddStep}
            disabled={!selectedMunicipality}
            className="d-flex align-items-center font-weight-bold"
            style={{ gap: '6px', fontSize: '13px' }}
          >
            <Plus size={14} />
            Add Step
          </Button>

          <Button
            variant="info"
            onClick={onSave}
            disabled={!selectedMunicipality || saving || !hasUnsavedChanges}
            className="d-flex align-items-center font-weight-bold"
            style={{ gap: '6px', fontSize: '13px' }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="d-flex align-items-center bg-white border-bottom py-2" style={{ gap: '4px' }}>
        {['All', ...INSTRUCTION_BUILDER_TABS].map(tab => {
          const isActive = activeTab === tab;

          return (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange(tab)}
              className={`btn btn-sm border-0 shadow-none font-weight-bold xmidFont rounded-pill px-3 py-1 transition-all ${!isActive &&
                'hover-muted'}`}
              style={{
                background: isActive ? '#111827' : 'transparent',
                color: isActive ? 'white' : '#6b7280',
                transition: 'background 0.12s, color 0.12s'
              }}
            >
              {tab}
              <span className="ml-1" style={{ fontSize: '11px', opacity: 0.7 }}>
                ({tabCounts[tab]})
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InstructionHeader;
